import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User as FirebaseUser,
  UserCredential,
  updateProfile,   
  setPersistence,
  browserLocalPersistence,
  OAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, onSnapshot, serverTimestamp, deleteDoc, limit, collectionGroup } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth, googleProvider, microsoftProvider, decodeToken, getUser, upsertUsersRootProfile } from '../utils/firebase';
import { User, Teacher, Student, resolveStudentClassId } from '../utils/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { isNativeApp } from '../utils/platform';
import { resolveTeacherPlan } from '../config/teacherPlans';

const SOCIAL_REDIRECT_KEY = 'maru_social_redirect_state';

interface AuthContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null | ((prev: User | null) => User | null)) => void;
  userRole: string | null;
  loading: boolean;
  error: string | null;
  subjectId: string | null;
  register: (userData: {
    email: string;
    password: string;
    name: string;
    role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
    schoolId: string;
    grade?: number;
    class?: number;
    number?: number;
    teacherId?: string;
    subject?: string;
    subjectId?: string;
    stampClasses?: Array<{
      grade: number;
      class: number;
      isActive: boolean;
    }>;
    homeroomClass?: {
      grade: number;
      class: number;
    };
    classRoles?: string[];
    isAdmin?: boolean;
    stampImageUrl?: string;
  }) => Promise<void>;
  registerWithSocial: (userData: {
    name: string;
    role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
    schoolId: string;
    schoolCode: string;
    grade?: number;
    class?: number;
    number?: number;
    teacherId?: string;
    subject?: string;
    subjectId?: string;
    stampClasses?: Array<{
      grade: number;
      class: number;
      isActive: boolean;
    }>;
    classRoles?: string[];
    homeroomClass?: {
      grade: number;
      class: number;
    };
    isAdmin?: boolean;
    stampImageUrl?: string;
  }) => Promise<{ success: boolean; message: string }>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: (selectedTab: 'student' | 'teacher') => Promise<void>;
  loginWithMicrosoft: (selectedTab: 'student' | 'teacher') => Promise<void>;
  updateUserProfile: (profileData: UserProfileData) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const convertToStudent = (studentDoc: any, userData: any, isSocial: boolean = false): Student => {
  const studentData = studentDoc.data();
  console.log('convertToStudent - studentData:', studentData);
  const classId = resolveStudentClassId(studentDoc, studentData);
  
  // assignedRoles 데이터 변환
  const assignedRoles = studentData.assignedRoles ? studentData.assignedRoles.map((role: any) => ({
    roleId: role.roleId,
    assignedAt: role.assignedAt?.toDate() || new Date(),
    isActive: role.isActive !== false
  })) : [];
  
  console.log('convertToStudent - assignedRoles:', assignedRoles);
  
  return {
    uid: isSocial ? studentDoc.id : undefined,  // 소셜 로그인일 때만 uid 설정
    email: userData.email || '',
    name: studentData.name,
    role: 'student' as const,
    username: userData.username,
    schoolId: studentData.schoolId,
    teacherId: studentData.teacherId,
    grade: Number(studentData.grade),
    class: Number(studentData.class),
    number: Number(studentData.number),
    stamps: studentData.stamps || [],
    loginType: isSocial ? 'social' : 'local',
    points: studentData.points || 0,
    isPointManager: studentData.isPointManager || false,
    isActive: studentData.isActive ?? true,
    createdAt: studentData.createdAt?.toDate() || new Date(),
    updatedAt: new Date(),
    assignedRoles: assignedRoles,
    roles: studentData.roles || [],
    classId,
    approvalStatus: studentData.approvalStatus || 'pending'
  };
};

const selectPreferredTeacherDoc = (docs: { ref: { path: string }; data: () => any }[]): typeof docs[0] | undefined => {
  if (docs.length === 0) return undefined;
  if (docs.length === 1) return docs[0];
  const activeSchoolId = typeof localStorage !== 'undefined' ? localStorage.getItem('maru_active_school_id') : null;
  if (activeSchoolId) {
    const preferred = docs.find(d => {
      const pathParts = d.ref.path.split('/');
      return pathParts[1] === 'schools' && pathParts[2] === activeSchoolId;
    });
    if (preferred) return preferred;
  }
  return docs[0];
};

const convertToTeacher = (teacherDoc: any, teacherData: any): Teacher => ({
  /** Firestore 규칙·쿼리는 Firebase Auth uid 기준 — 문서 ID와 다를 수 있어 필드 uid 우선 */
  uid: (typeof teacherData.uid === 'string' && teacherData.uid.length > 0) ? teacherData.uid : teacherDoc.id,
  email: teacherData.email || '',
  name: teacherData.name,
  role: teacherData.role || 'teacher',
  schoolId: teacherData.schoolId,
  subject: teacherData.subject || '',
  homeroomClass: teacherData.homeroomClass,
  stampClasses: teacherData.stampClasses || [],
  classRoles: teacherData.classRoles || [],
  isActive: teacherData.isActive ?? true,
  createdAt: teacherData.createdAt?.toDate() || new Date(),
  updatedAt: new Date(),
  subjectId: teacherData.subjectId || '',
  classId: teacherData.homeroomClass ? `${teacherData.schoolId}_${teacherData.homeroomClass.grade}_${teacherData.homeroomClass.class}` : undefined,
  isAdmin: teacherData.role === 'superAdmin' || teacherData.role === 'schoolAdmin',
  loginType: 'social',
  plan: resolveTeacherPlan(teacherData.plan, teacherData.role)
});

interface AuthProviderProps {
  children: ReactNode;
}

interface UserProfileData {
  name: string;
  email: string;
  subject?: string;
  grade?: number;
  class?: number;
  number?: number;
  homeroomClass?: {
    grade: number;
    class: number;
  };
  stampClasses?: Array<{
    grade: number;
    class: number;
    isActive: boolean;
  }>;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // iOS/Android 앱: redirect 복귀 시 소셜 로그인 결과 처리 (팝업 대신 전체 화면 OAuth 사용)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (import.meta.env.DEV) {
        console.debug('[Auth] redirect 결과 확인 중…');
      }
      const result = await getRedirectResult(auth);
      if (import.meta.env.DEV) {
        console.debug('[Auth] getRedirectResult:', result ? '성공' : '없음');
      }
      if (cancelled) return;
      if (!result) {
        return;
      }
      if (import.meta.env.DEV) {
        console.debug('[Auth] redirect 복귀', { email: result.user?.email });
      }
      const raw = sessionStorage.getItem(SOCIAL_REDIRECT_KEY);
      if (import.meta.env.DEV) {
        console.debug('[Auth] redirect sessionStorage:', raw ? '있음' : '없음');
      }
      sessionStorage.removeItem(SOCIAL_REDIRECT_KEY);
      if (!raw) {
        console.warn('⚠️ sessionStorage에 저장된 정보가 없습니다. redirect 상태가 손실되었을 수 있습니다.');
        setLoading(false);
        return;
      }
      try {
        const { provider, selectedTab, from } = JSON.parse(raw) as {
          provider: 'google' | 'microsoft';
          selectedTab: 'student' | 'teacher';
          from: string;
        };
        if (import.meta.env.DEV) {
          console.debug('[Auth] redirect 파싱', { provider, selectedTab, from });
        }
        if (provider === 'google') {
          await processGoogleAuthResult(result.user, selectedTab, from);
        } else {
          await processMicrosoftAuthResult(result.user, selectedTab, from);
        }
        if (import.meta.env.DEV) {
          console.debug('[Auth] 소셜 로그인 redirect 처리 완료');
        }
      } catch (e) {
        console.error('❌ 소셜 로그인 redirect 처리 오류:', e);
        setError(e instanceof Error ? e.message : '소셜 로그인 처리 중 오류가 발생했습니다.');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Firebase Auth 영구성 설정
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error('Auth persistence 설정 중 오류:', error);
    });
    // 로그인 상태 변경 리스너
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (import.meta.env.DEV) {
        console.debug('[Auth] onAuthStateChanged', user ? { uid: user.uid, email: user.email } : 'signed out');
      }

      if (user) {
        try {
          const token = await user.getIdToken();
          const decodedToken = await decodeToken(token);
          if (import.meta.env.DEV && decodedToken?.claims) {
            const c = decodedToken.claims as Record<string, unknown>;
            console.debug('[Auth] custom claims', { role: c.role, schoolId: c.schoolId });
          }
          
          // 토큰에서 사용자 정보 추출
          const claims = decodedToken?.claims || {};
          const role = claims.role as 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin' | undefined;
          const schoolId = claims.schoolId as string | undefined;
          const classId = claims.classId as string | undefined;

          // 1) users/{uid} 우선 → superAdmin/schoolAdmin 역할 유지
          // 2) 없으면 레거시 학생/교사 경로
          let userData = await getUser(user.uid);

          if (!userData) {
            if (role === 'student') {
              const studentQuery = query(
                collectionGroup(db, 'students'),
                where('uid', '==', user.uid)
              );
              const studentSnapshot = await getDocs(studentQuery);
              if (!studentSnapshot.empty) {
                const studentDoc = studentSnapshot.docs[0];
                userData = convertToStudent(studentDoc, studentDoc.data(), true);
              } else {
                const legacyStudentQuery = query(
                  collection(db, 'students'),
                  where('uid', '==', user.uid)
                );
                const legacyStudentSnapshot = await getDocs(legacyStudentQuery);
                if (!legacyStudentSnapshot.empty) {
                  const studentDoc = legacyStudentSnapshot.docs[0];
                  userData = convertToStudent(studentDoc, studentDoc.data(), true);
                }
              }
            } else {
              const teacherQuery = query(
                collectionGroup(db, 'teachers'),
                where('uid', '==', user.uid)
              );
              const teacherSnapshot = await getDocs(teacherQuery);
              if (!teacherSnapshot.empty) {
                const teacherDoc = selectPreferredTeacherDoc(teacherSnapshot.docs);
                if (teacherDoc) {
                  userData = convertToTeacher(teacherDoc, teacherDoc.data());
                }
              }
            }
          }

          if (!schoolId) {
            const canProceed =
              userData &&
              (userData.role === 'superAdmin' ||
                userData.role === 'schoolAdmin' ||
                !!userData.schoolId);
            if (!canProceed) {
              console.error('학교 코드가 없습니다.');
              const currentPath = window.location.pathname;
              const isRegisterPage =
                currentPath.includes('/StudentRegister') ||
                currentPath.includes('/TeacherRegister') ||
                currentPath.includes('/studentregister') ||
                currentPath.includes('/teacherregister');
              if (!isRegisterPage) {
                await signOut(auth);
                navigate('/login');
              }
              setLoading(false);
              return;
            }
          }

          if (userData) {
            setCurrentUser(userData);
            setUserRole(userData.role);
          } else {
            console.error('사용자 정보를 찾을 수 없습니다.');
            
            // 현재 경로가 회원가입 페이지인지 확인
            const currentPath = window.location.pathname;
            const isRegisterPage = currentPath.includes('/StudentRegister') || 
                                  currentPath.includes('/TeacherRegister') ||
                                  currentPath.includes('/studentregister') ||
                                  currentPath.includes('/teacherregister');
            
            if (!isRegisterPage) {
              await signOut(auth);
              navigate('/login');
            }
          }
        } catch (error) {
          console.error('사용자 정보 로드 중 오류:', error);
          
          // 현재 경로가 회원가입 페이지인지 확인
          const currentPath = window.location.pathname;
          const isRegisterPage = currentPath.includes('/StudentRegister') || 
                                currentPath.includes('/TeacherRegister') ||
                                currentPath.includes('/studentregister') ||
                                currentPath.includes('/teacherregister');
          
          if (!isRegisterPage) {
            await signOut(auth);
            navigate('/login');
          }
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  // Google 소셜 로그인 결과 처리 (팝업/redirect 공통)
  const processGoogleAuthResult = async (
    user: FirebaseUser,
    selectedTab: 'student' | 'teacher',
    from: string
  ) => {
    // 토큰에서 사용자 정보 가져오기
    const token = await user.getIdToken();
    const decodedToken = await decodeToken(token);
    
    if (!decodedToken) {
      throw new Error('토큰 디코딩 실패');
    }

    // 커스텀 클레임에서 사용자 정보 가져오기
    const { role, schoolId } = decodedToken.claims as { role: string; schoolId: string };
    
    // 학교 정보가 없는 경우 회원가입 페이지로 이동 (세션 유지 — users 프로필 저장에 필요)
    if (!schoolId) {
      if (selectedTab === 'teacher') {
        navigate('/teacherregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
      } else {
        navigate('/studentregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
      }
      return;
    }

    let userData;
    
    if (selectedTab === 'student') {
      // 학생 정보 가져오기 (collectionGroup으로 조회)
      const studentsQuery = query(
        collectionGroup(db, 'students'),
        where('uid', '==', user.uid)
      );
      const studentSnapshot = await getDocs(studentsQuery);
      
      if (studentSnapshot.empty) {
        // collectionGroup에서 찾지 못한 경우 기존 students 컬렉션에서도 확인
        const legacyStudentQuery = query(
          collection(db, 'students'),
          where('uid', '==', user.uid)
        );
        const legacyStudentSnapshot = await getDocs(legacyStudentQuery);
        
        if (legacyStudentSnapshot.empty) {
          // 학생 정보가 없는 경우 등록 페이지로 이동 (세션 유지)
          navigate('/studentregister', { 
            state: { 
              email: user.email,
              name: user.displayName,
              loginType: 'social',
              isSocialLogin: true,
              uid: user.uid,
              from: from
            }
          });
          return;
        } else {
          const studentDoc = legacyStudentSnapshot.docs[0];
          userData = {
            ...studentDoc.data(),
            uid: user.uid,
            email: user.email || '',
            name: user.displayName || '',
            role: 'student' as const,
            schoolId,
            loginType: 'social' as const
          };
        }
      } else {
        const studentDoc = studentSnapshot.docs[0];
        userData = {
          ...studentDoc.data(),
          uid: user.uid,
          email: user.email || '',
          name: user.displayName || '',
          role: 'student' as const,
          schoolId,
          loginType: 'social' as const
        };
      }
    } else if (selectedTab === 'teacher') {
      // 교사 정보 가져오기
      const teachersQuery = query(
        collectionGroup(db, 'teachers'),
        where('uid', '==', user.uid)
      );
      const teacherSnapshot = await getDocs(teachersQuery);
      
      if (teacherSnapshot.empty) {
        // 교사 정보가 없는 경우 등록 페이지로 이동 (세션 유지)
        navigate('/teacherregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
        return;
      }
      
      const teacherDoc = selectPreferredTeacherDoc(teacherSnapshot.docs);
      if (!teacherDoc) {
        return;
      }
      userData = {
        ...teacherDoc.data(),
        uid: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        role: 'teacher' as const,
        schoolId,
        loginType: 'social' as const
      };
    }

    if (!userData) {
      // 사용자 정보가 없는 경우 등록 페이지로 이동 (세션 유지)
      if (selectedTab === 'teacher') {
        navigate('/teacherregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
      } else {
        navigate('/studentregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
      }
      return;
    }

    setCurrentUser(userData);
    setUserRole(selectedTab);
    
    // 로그인 후 리다이렉트: /makecodearcade 경로는 원래 경로로, 보호된 경로는 기본 페이지로
    if (from.startsWith('/makecodearcade')) {
      navigate(from);
    } else if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
      if (selectedTab === 'teacher') {
        navigate('/class/teacher/ClassTeacherHome');
      } else if (selectedTab === 'student') {
        navigate('/class/student');
      }
    } else {
      navigate(from);
    }
  };

  // Microsoft 소셜 로그인 결과 처리 (팝업/redirect 공통)
  const processMicrosoftAuthResult = async (
    user: FirebaseUser,
    selectedTab: 'student' | 'teacher',
    from: string
  ) => {
    // 선택된 탭에 따라 처리
    if (selectedTab === 'teacher') {
      // 교사 문서 먼저 확인
      try {
        const teacherQuery = query(
          collectionGroup(db, 'teachers'),
          where('uid', '==', user.uid),
        );
        const teacherSnapshot = await getDocs(teacherQuery);
        
        if (!teacherSnapshot.empty) {
          const teacherDoc = selectPreferredTeacherDoc(teacherSnapshot.docs);
          if (!teacherDoc) {
            return;
          }
          const teacherData = teacherDoc.data();
          setCurrentUser(convertToTeacher(teacherDoc, teacherData));
          setUserRole(teacherData.role || 'teacher');
          
          // 슈퍼관리자나 교사인 경우 교사 메뉴로 이동
          if (teacherData.role === 'superAdmin' || teacherData.role === 'schoolAdmin' || teacherData.role === 'teacher') {
            // 로그인 후 리다이렉트: /makecodearcade 경로는 원래 경로로, 보호된 경로는 기본 페이지로
            if (from.startsWith('/makecodearcade')) {
              navigate(from);
            } else if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
              navigate('/class/teacher/ClassTeacherHome');
            } else {
              navigate(from);
            }
          }
          return;
        } else {
          // 교사 문서가 없는 경우 등록 페이지로 이동
          navigate('/teacherregister', { 
            state: { 
              email: user.email,
              name: user.displayName,
              loginType: 'social',
              isSocialLogin: true,
              uid: user.uid,
              from: from
            }
          });
          return;
        }
      } catch (error) {
        console.error('교사 데이터 확인 중 오류:', error);
        navigate('/teacherregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
        return;
      }
    } else if (selectedTab === 'student') {
      // 학생 문서 확인 (UID로 직접 조회)
      try {
        const studentQuery = query(
          collectionGroup(db, 'students'),
          where('uid', '==', user.uid),
        );
        const studentSnapshot = await getDocs(studentQuery);
        
        if (!studentSnapshot.empty) {
          const studentDoc = studentSnapshot.docs[0];
          const studentData = studentDoc.data();
          setCurrentUser(convertToStudent(studentDoc, studentData, true));
          setUserRole('student');
          
          // 로그인 후 리다이렉트: /makecodearcade 경로는 원래 경로로, 보호된 경로는 기본 페이지로
          if (from.startsWith('/makecodearcade')) {
            navigate(from);
          } else if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
            navigate('/class/student');
          } else {
            navigate(from);
          }
          return;
        } else {
          // 학생 문서가 없는 경우 등록 페이지로 이동
          navigate('/studentregister', { 
            state: { 
              email: user.email,
              name: user.displayName,
              loginType: 'social',
              isSocialLogin: true,
              uid: user.uid,
              from: from
            }
          });
          return;
        }
      } catch (error) {
        console.error('학생 데이터 확인 중 오류:', error);
        navigate('/studentregister', { 
          state: { 
            email: user.email,
            name: user.displayName,
            loginType: 'social',
            isSocialLogin: true,
            uid: user.uid,
            from: from
          }
        });
        return;
      }
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    name: string;
    role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
    schoolId: string;
    grade?: number;
    class?: number;
    number?: number;
    teacherId?: string;
    subject?: string;
    subjectId?: string;
    stampClasses?: Array<{
      grade: number;
      class: number;
      isActive: boolean;
    }>;
    homeroomClass?: {
      grade: number;
      class: number;
    };
    classRoles?: string[];
    isAdmin?: boolean;
    stampImageUrl?: string;
  }) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      
      const baseUserData = {
        uid: result.user.uid,
        localId: result.user.uid,  // localId 추가
        email: result.user.email || '',
        name: userData.name,
        role: userData.role,
        schoolId: userData.schoolId,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (userData.role === 'student') {
        const studentData: Student = {
          ...baseUserData,
          role: 'student' as const,
          grade: Number(userData.grade || ''),
          class: Number(userData.class || ''),
          teacherId: userData.teacherId || '',
          stamps: [],
          loginType: 'local',
          username: userData.email,
          points: 0,
          number: Number(userData.number || ''),
          assignedRoles: [],
          roles: [],
          classId: `temp_${result.user.uid}`,
          approvalStatus: 'pending'
        };
        await setDoc(doc(db, 'students', result.user.uid), studentData);
        await upsertUsersRootProfile({
          uid: result.user.uid,
          email: baseUserData.email,
          name: userData.name,
          role: 'student',
          schoolId: userData.schoolId,
          loginType: 'local',
        });
        setCurrentUser(studentData);
      } else {
        const teacherData: Teacher = {
          ...baseUserData,
          role: 'teacher' as const,
          subject: userData.subject || '',
          subjectId: userData.subjectId || '',
          homeroomClass: userData.homeroomClass,
          stampClasses: userData.stampClasses || [],
          classRoles: userData.classRoles || [],
          stampImageUrl: userData.stampImageUrl,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          loginType: 'local'
        };
        await setDoc(doc(db, 'teachers', result.user.uid), teacherData);
        await upsertUsersRootProfile({
          uid: result.user.uid,
          email: baseUserData.email,
          name: userData.name,
          role:
            userData.role === 'schoolAdmin'
              ? userData.role
              : 'teacher',
          schoolId: userData.schoolId,
          loginType: 'local',
        });
        setCurrentUser(teacherData);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const registerWithSocial = async (userData: {
    name: string;
    role: 'student' | 'teacher' | 'schoolAdmin' | 'superAdmin';
    schoolId: string;
    schoolCode: string;
    grade?: number;
    class?: number;
    number?: number;
    teacherId?: string;
    subject?: string;
    subjectId?: string;
    stampClasses?: Array<{
      grade: number;
      class: number;
      isActive: boolean;
    }>;
    classRoles?: string[];
    homeroomClass?: {
      grade: number;
      class: number;
    };
    isAdmin?: boolean;
    stampImageUrl?: string;
  }) => {
    try {
      if (!currentUser?.uid) {
        throw new Error('사용자 정보를 찾을 수 없습니다.');
      }

      // 이미 승인 요청이 있는지 확인
      const requestRef = doc(db, 'teacher_requests', currentUser.uid);
      const requestDoc = await getDoc(requestRef);
      
      if (requestDoc.exists()) {
        console.log('이미 승인 요청이 존재함');
        navigate('/');
        return { success: true, message: '이미 승인 요청이 진행 중입니다. 관리자의 승인을 기다려주세요.' };
      }

      console.log('소셜 로그인 사용자 정보:', {
        uid: currentUser.uid,
        email: currentUser.email,
        name: userData.name,
        role: userData.role,
        schoolId: userData.schoolId
      });

      // 승인 요청 데이터 생성 (필수 필드만 포함)
      const requestData = {
        userId: currentUser.uid,
        email: currentUser.email,
        name: userData.name,
        role: 'teacher',
        schoolId: userData.schoolId,
        schoolCode: userData.schoolCode,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // 승인 요청 저장
      await setDoc(doc(db, 'teacher_requests', currentUser.uid), requestData);

      await upsertUsersRootProfile({
        uid: currentUser.uid,
        email: currentUser.email || requestData.email || '',
        name: userData.name,
        role: 'teacher',
        schoolId: userData.schoolId,
        loginType: 'social',
      });

      // 사용자 정보 업데이트
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        await updateProfile(firebaseUser, {
          displayName: userData.name
        });
      }

      console.log('소셜 로그인 승인 요청 완료');
      // 승인 요청 완료 후 홈으로 리다이렉션
      navigate('/');
      return { success: true, message: '관리자 승인을 기다리고 있습니다.' };
    } catch (error) {
      console.error('소셜 로그인 등록 중 오류:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setCurrentUser(null);
      setUserRole(null);
      setSubjectId(null);
      // 홈페이지로 리다이렉트
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
      setError('로그아웃에 실패했습니다.');
      throw err;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      // 로그인 전 경로 저장
      const from = location.state?.from?.pathname || '/';

      // 학생 문서 확인 (아이디로 조회)
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, where('username', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const studentDoc = querySnapshot.docs[0];
        const studentData = studentDoc.data();
        
        // 비밀번호 확인
        if (studentData.password !== password) {
          throw new Error('비밀번호가 일치하지 않습니다.');
        }

        // 인증 상태 유지
        await setPersistence(auth, browserLocalPersistence);

        setCurrentUser(convertToStudent(studentDoc, studentData));
        setLoading(false);
        
        // 로그인 후 리다이렉트: /makecodearcade 경로는 원래 경로로, 보호된 경로는 기본 페이지로
        if (from.startsWith('/makecodearcade')) {
          navigate(from);
        } else if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
          navigate('/class/student');
        } else {
          navigate(from);
        }
        return;
      }

      // 교사 문서 확인 (Firebase Auth 사용)
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      // users/{uid}의 role(superAdmin 등)을 반영 — 루트 teachers 문서만 보던 방식은 역할이 틀어짐
      const profile = await getUser(user.uid);
      if (
        profile &&
        (profile.role === 'teacher' ||
          profile.role === 'schoolAdmin' ||
          profile.role === 'superAdmin')
      ) {
        setCurrentUser(profile);
        setUserRole(profile.role);
        if (from.startsWith('/makecodearcade')) {
          navigate(from);
        } else if (
          from.startsWith('/class/') ||
          from.startsWith('/admin/') ||
          from.startsWith('/subject/')
        ) {
          if (profile.role === 'superAdmin' || profile.role === 'schoolAdmin') {
            navigate(from.startsWith('/admin') ? from : '/class/teacher/ClassTeacherHome');
          } else {
            navigate('/class/teacher/ClassTeacherHome');
          }
        } else {
          navigate(from);
        }
        return;
      }

      throw new Error('사용자 정보를 찾을 수 없습니다.');
    } catch (err) {
      console.error('로그인 중 오류:', err);
      setError('로그인에 실패했습니다.');
      setLoading(false);
    }
  };

  const loginWithGoogle = async (selectedTab: 'student' | 'teacher') => {
    try {
      setLoading(true);
      setError(null);
      
      const isNative = isNativeApp();
      console.log('Google 로그인 시작, selectedTab:', selectedTab, 'isNativeApp:', isNative);
      console.log('Capacitor platform:', typeof window !== 'undefined' && (window as any).Capacitor?.getPlatform?.());
      
      // 로그인 전 경로 저장
      const from = location.state?.from?.pathname || '/';
      
      // iOS/Android 앱에서는 redirect만 사용 (팝업은 WebView에서 차단됨)
      if (isNative) {
        console.log('네이티브 앱: redirect 방식 사용');
        try {
          sessionStorage.setItem(SOCIAL_REDIRECT_KEY, JSON.stringify({
            provider: 'google',
            selectedTab,
            from
          }));
          console.log('sessionStorage 저장 완료, redirect 시작...');
          await signInWithRedirect(auth, googleProvider);
          console.log('signInWithRedirect 호출 완료 - 페이지가 리디렉트됩니다');
          // redirect로 페이지가 이동하므로 여기서 종료
          return;
        } catch (redirectError: any) {
          console.error('Redirect 중 에러:', redirectError);
          // redirect 에러는 사용자에게 표시
          if (redirectError.code === 'auth/unauthorized-domain') {
            setError('로그인 도메인이 허용되지 않았습니다.\n\nFirebase Console > Authentication > Settings > Authorized domains에 "maruedu.kr"이 추가되어 있는지 확인하세요.');
          } else {
            setError(redirectError.message || 'Google 로그인에 실패했습니다.');
          }
          setLoading(false);
          throw redirectError; // 상위 catch로 전달
        }
      }
      
      // 웹에서는 팝업 사용
      console.log('웹: 팝업 방식 사용');
      const result = await signInWithPopup(auth, googleProvider);
      await processGoogleAuthResult(result.user, selectedTab, from);
    } catch (error: any) {
      console.error('Google 로그인 오류:', error);
      if (error.code === 'auth/unauthorized-domain') {
        setError('로그인 도메인이 허용되지 않았습니다. Firebase Console에서 도메인을 추가해주세요.');
      } else {
        setError(error instanceof Error ? error.message : '로그인 중 오류가 발생했습니다.');
      }
      setLoading(false);
      throw error; // Login.tsx에서 처리할 수 있도록 에러 전달
    }
  };

  const loginWithMicrosoft = async (selectedTab: 'student' | 'teacher') => {
    try {
      setLoading(true);
      setError(null);
      const isNative = isNativeApp();
      console.log('마이크로소프트 로그인 시작, selectedTab:', selectedTab, 'isNativeApp:', isNative);
      console.log('Capacitor platform:', typeof window !== 'undefined' && (window as any).Capacitor?.getPlatform?.());
      
      // 로그인 전 경로 저장
      const from = location.state?.from?.pathname || '/';
      
      // iOS/Android 앱에서는 redirect만 사용 (팝업은 WebView에서 차단됨)
      if (isNative) {
        console.log('네이티브 앱: redirect 방식 사용');
        try {
          sessionStorage.setItem(SOCIAL_REDIRECT_KEY, JSON.stringify({
            provider: 'microsoft',
            selectedTab,
            from
          }));
          console.log('sessionStorage 저장 완료, redirect 시작...');
          await signInWithRedirect(auth, microsoftProvider);
          console.log('signInWithRedirect 호출 완료 - 페이지가 리디렉트됩니다');
          // redirect로 페이지가 이동하므로 여기서 종료
          return;
        } catch (redirectError: any) {
          console.error('Redirect 중 에러:', redirectError);
          // redirect 에러는 사용자에게 표시
          if (redirectError.code === 'auth/unauthorized-domain') {
            setError('로그인 도메인이 허용되지 않았습니다.\n\nFirebase Console > Authentication > Settings > Authorized domains에 "maruedu.kr"이 추가되어 있는지 확인하세요.');
          } else {
            setError(redirectError.message || '마이크로소프트 로그인에 실패했습니다.');
          }
          setLoading(false);
          throw redirectError; // 상위 catch로 전달
        }
      }
      
      // 웹에서는 팝업 사용
      console.log('웹: 팝업 방식 사용');
      const result = await signInWithPopup(auth, microsoftProvider);
      console.log('마이크로소프트 로그인 성공:', result.user);
      await processMicrosoftAuthResult(result.user, selectedTab, from);
    } catch (err: any) {
      console.error('마이크로소프트 로그인 오류:', err);
      
      if (err.code === 'auth/unauthorized-domain') {
        setError('로그인 도메인이 허용되지 않았습니다. Firebase Console에서 도메인을 추가해주세요.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('로그인 창이 닫혔습니다. 다시 시도해주세요.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('로그인이 취소되었습니다. 다시 시도해주세요.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      } else {
        setError(err.message || '마이크로소프트 로그인에 실패했습니다.');
      }
      setLoading(false);
      throw err; // Login.tsx에서 처리할 수 있도록 에러 전달
    }
  };

  const updateUserProfile = async (profileData: UserProfileData) => {
    if (!currentUser?.uid) throw new Error('로그인이 필요합니다.');

    try {
      const userRef = doc(db, userRole === 'teacher' ? 'teachers' : 'students', currentUser.uid);
      await updateDoc(userRef, {
        name: profileData.name,
        subject: profileData.subject,
        grade: profileData.grade,
        class: profileData.class,
        number: profileData.number,
        homeroomClass: profileData.homeroomClass,
        stampClasses: profileData.stampClasses,
        updatedAt: serverTimestamp()
      });

      setCurrentUser((prev: User | null) => {
        if (!prev || (prev.role !== 'teacher' && prev.role !== 'superAdmin')) {
          console.log('이전 사용자 정보가 없거나 교사/관리자가 아님:', prev);
          return null;
        }
        const updatedUser = {
          ...prev,
          homeroomClass: profileData.homeroomClass || undefined,
          classId: profileData.homeroomClass ? `${prev.schoolId}_${profileData.homeroomClass.grade}_${profileData.homeroomClass.class}` : undefined,
          stampClasses: profileData.stampClasses || [],
          subject: profileData.subject,
          name: profileData.name
        };
        console.log('업데이트된 사용자 정보:', updatedUser);
        return updatedUser;
      });
    } catch (error: any) {
      throw new Error('프로필 업데이트에 실패했습니다: ' + error.message);
    }
  };

  const deleteAccount = async () => {
    try {
      if (!currentUser?.uid) {
        throw new Error('로그인된 사용자가 없습니다.');
      }

      const functions = getFunctions(getApp());
      const deleteOwnAccount = httpsCallable(functions, 'deleteOwnAccount');
      await deleteOwnAccount({});

      setCurrentUser(null);
      setUserRole(null);
      setError(null);
      try {
        await logout();
      } catch (logoutError) {
        console.error('계정 삭제 후 로그아웃:', logoutError);
      }
    } catch (error: any) {
      console.error('계정 삭제 중 오류:', error);
      setError(error.message);
      throw error;
    }
  };

  const value = {
    currentUser,
    setCurrentUser,
    userRole,
    loading,
    error,
    subjectId,
    register,
    registerWithSocial,
    login,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    updateUserProfile,
    deleteAccount
  };

  // children(라우트 트리)은 항상 Provider 안에서 마운트해야 합니다.
  // {!loading && children}처럼 로딩 중에 라우트를 통째로 언마운트하면 Strict Mode/HMR 등에서
  // ProtectedRoute 등이 컨텍스트 밖에서 그려져 useAuth 오류가 날 수 있습니다.
  // 인증 대기 UI는 각 라우트·ProtectedRoute의 loading 분기로 처리합니다.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};